/**
 * TipTap Rich Text Editor Component
 * 
 * A JIRA-like rich text editor built with TipTap (ProseMirror foundation).
 * Supports formatting, @mentions, emoji, images, code blocks with syntax highlighting.
 * Converts between TipTap JSON and JIRA's ADF format.
 */
import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import { common, createLowlight } from 'lowlight';
import { compressImageFile } from '../utils/imageCompression';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemText,
  Popover,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
  Link as LinkIcon,
  LinkOff,
  Image as ImageIcon,
  EmojiEmotions,
  Title,
} from '@mui/icons-material';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

import MentionList from './MentionList';
import { adfToTiptap, tiptapToAdf } from '../utils/adfConverter';

// Initialize lowlight with common languages
const lowlight = createLowlight(common);

// CSS styles for the editor
const editorStyles = {
  container: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    backgroundColor: 'background.paper',
    '&:focus-within': {
      borderColor: 'primary.main',
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.5,
    p: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'grey.50',
  },
  editor: {
    '& .ProseMirror': {
      minHeight: 120,
      maxHeight: 400,
      overflow: 'auto',
      padding: '12px 16px',
      outline: 'none',
      
      // Typography
      '& p': {
        margin: '0 0 8px 0',
        '&:last-child': { margin: 0 },
      },
      
      // Headings
      '& h1, & h2, & h3': {
        margin: '16px 0 8px 0',
        fontWeight: 600,
      },
      '& h1': { fontSize: '1.5rem' },
      '& h2': { fontSize: '1.25rem' },
      '& h3': { fontSize: '1.1rem' },
      
      // Lists
      '& ul, & ol': {
        margin: '8px 0',
        paddingLeft: '24px',
      },
      '& li': {
        margin: '4px 0',
      },
      
      // Blockquote
      '& blockquote': {
        borderLeft: '3px solid #ddd',
        paddingLeft: '16px',
        margin: '8px 0',
        color: 'text.secondary',
      },
      
      // Code
      '& code': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderRadius: '3px',
        padding: '2px 4px',
        fontFamily: 'monospace',
        fontSize: '0.9em',
      },
      '& pre': {
        backgroundColor: '#1e1e1e',
        borderRadius: '4px',
        padding: '12px 16px',
        margin: '8px 0',
        overflow: 'auto',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0,
          color: '#d4d4d4',
        },
      },
      
      // Links
      '& a': {
        color: 'primary.main',
        textDecoration: 'underline',
        cursor: 'pointer',
      },
      
      // Images
      '& img': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '4px',
        margin: '8px 0',
      },
      
      // Mentions
      '& .mention': {
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderRadius: '4px',
        padding: '2px 4px',
        color: 'primary.main',
        fontWeight: 500,
      },
      
      // Placeholder
      '& p.is-editor-empty:first-child::before': {
        content: 'attr(data-placeholder)',
        color: 'text.disabled',
        float: 'left',
        pointerEvents: 'none',
        height: 0,
      },
      
      // Horizontal rule
      '& hr': {
        border: 'none',
        borderTop: '1px solid #ddd',
        margin: '16px 0',
      },
    },
  },
};

// Toolbar button component
const ToolbarButton = ({ icon, tooltip, active, disabled, onClick }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderRadius: 0.5,
          backgroundColor: active ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: active ? 'action.selected' : 'action.hover',
          },
        }}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

const TiptapEditor = forwardRef(({
  initialContent = null, // ADF format content
  placeholder = 'Write something...',
  onChange,
  onSubmit,
  disabled = false,
  mentionableUsers = [],
  onImageUpload,
  minHeight = 120,
  maxHeight = 400,
  autoFocus = false,
}, ref) => {
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [headingAnchor, setHeadingAnchor] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const mentionListRef = useRef(null);

  // Configure mention extension with proper suggestion handling
  const mentionConfig = {
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: {
      items: ({ query }) => {
        return mentionableUsers
          .filter(user => 
            user.label?.toLowerCase().includes(query.toLowerCase()) ||
            user.email?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 10);
      },
      render: () => {
        let popup = null;
        let root = null;

        return {
          onStart: (props) => {
            popup = document.createElement('div');
            popup.className = 'mention-popup';
            popup.style.cssText = `
              position: fixed;
              z-index: 9999;
              background: white;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              max-height: 300px;
              overflow-y: auto;
              min-width: 200px;
            `;

            const rect = props.clientRect?.();
            if (rect) {
              popup.style.left = `${rect.left}px`;
              popup.style.top = `${rect.bottom + 4}px`;
            }

            document.body.appendChild(popup);

            // Dynamic import for React 18 createRoot
            import('react-dom/client').then(({ createRoot }) => {
              root = createRoot(popup);
              root.render(
                <MentionList
                  items={props.items}
                  command={props.command}
                />
              );
            });
          },

          onUpdate: (props) => {
            if (popup && root) {
              const rect = props.clientRect?.();
              if (rect) {
                popup.style.left = `${rect.left}px`;
                popup.style.top = `${rect.bottom + 4}px`;
              }

              root.render(
                <MentionList
                  items={props.items}
                  command={props.command}
                />
              );
            }
          },

          onKeyDown: (props) => {
            if (props.event.key === 'Escape') {
              popup?.remove();
              root?.unmount();
              return true;
            }
            return false;
          },

          onExit: () => {
            popup?.remove();
            root?.unmount();
          },
        };
      },
    },
  };

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We'll use CodeBlockLowlight instead
      }),
      Placeholder.configure({
        placeholder,
      }),
      Mention.configure(mentionConfig),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      TextStyle,
      Color,
      Underline,
    ],
    content: initialContent ? adfToTiptap(initialContent) : '',
    editable: !disabled,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const tiptapJson = editor.getJSON();
        const adfContent = tiptapToAdf(tiptapJson);
        onChange(adfContent);
      }
    },
  });

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getContent: () => {
      if (!editor) return null;
      return tiptapToAdf(editor.getJSON());
    },
    setContent: (adfContent) => {
      if (!editor) return;
      const tiptapContent = adfToTiptap(adfContent);
      editor.commands.setContent(tiptapContent);
    },
    clearContent: () => {
      if (!editor) return;
      editor.commands.clearContent();
    },
    focus: () => {
      editor?.commands.focus();
    },
    isEmpty: () => {
      return editor?.isEmpty ?? true;
    },
  }));

  // Update content when initialContent changes
  useEffect(() => {
    if (editor && initialContent) {
      const tiptapContent = adfToTiptap(initialContent);
      editor.commands.setContent(tiptapContent);
    }
  }, [initialContent, editor]);

  // Handle image upload
  const handleImageUpload = useCallback(async (file) => {
    // Compress image client-side before preview/upload
    const compressed = await compressImageFile(file);

    if (!onImageUpload) {
      // Fallback: convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        editor?.chain().focus().setImage({ src: e.target.result }).run();
      };
      reader.readAsDataURL(compressed);
      return;
    }

    try {
      setIsUploading(true);
      const imageUrl = await onImageUpload(compressed);
      editor?.chain().focus().setImage({ src: imageUrl }).run();
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setIsUploading(false);
    }
  }, [editor, onImageUpload]);

  // Handle file input change
  const handleFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
    event.target.value = '';
  }, [handleImageUpload]);

  // Handle paste (for images)
  useEffect(() => {
    if (!editor) return;

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          break;
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('paste', handlePaste);
    return () => editorElement.removeEventListener('paste', handlePaste);
  }, [editor, handleImageUpload]);

  // Handle drag and drop
  useEffect(() => {
    if (!editor) return;

    const handleDrop = (event) => {
      const files = event.dataTransfer?.files;
      if (!files?.length) return;

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          event.preventDefault();
          handleImageUpload(file);
          break;
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('drop', handleDrop);
    return () => editorElement.removeEventListener('drop', handleDrop);
  }, [editor, handleImageUpload]);

  // Handle link insertion
  const handleSetLink = () => {
    if (!linkUrl) {
      editor?.chain().focus().unsetLink().run();
    } else {
      let url = linkUrl;
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }
      editor?.chain().focus().setLink({ href: url }).run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    editor?.chain().focus().insertContent(emoji.native).run();
    setEmojiAnchor(null);
  };

  // Handle heading selection
  const handleHeadingSelect = (level) => {
    if (level === 0) {
      editor?.chain().focus().setParagraph().run();
    } else {
      editor?.chain().focus().toggleHeading({ level }).run();
    }
    setHeadingAnchor(null);
  };

  if (!editor) {
    return null;
  }

  return (
    <Box sx={editorStyles.container}>
      {/* Toolbar */}
      <Box sx={editorStyles.toolbar}>
        {/* Text formatting */}
        <ToolbarButton
          icon={<FormatBold fontSize="small" />}
          tooltip="Bold (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<FormatItalic fontSize="small" />}
          tooltip="Italic (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<FormatUnderlined fontSize="small" />}
          tooltip="Underline (Ctrl+U)"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<StrikethroughS fontSize="small" />}
          tooltip="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<Code fontSize="small" />}
          tooltip="Inline code"
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={disabled}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Headings */}
        <ToolbarButton
          icon={<Title fontSize="small" />}
          tooltip="Headings"
          active={editor.isActive('heading')}
          onClick={(e) => setHeadingAnchor(e.currentTarget)}
          disabled={disabled}
        />
        <Menu
          anchorEl={headingAnchor}
          open={Boolean(headingAnchor)}
          onClose={() => setHeadingAnchor(null)}
        >
          <MenuItem onClick={() => handleHeadingSelect(0)}>
            <ListItemText>Normal text</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(1)}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }}>Heading 1</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(2)}>
            <ListItemText primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}>Heading 2</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(3)}>
            <ListItemText primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}>Heading 3</ListItemText>
          </MenuItem>
        </Menu>

        {/* Lists */}
        <ToolbarButton
          icon={<FormatListBulleted fontSize="small" />}
          tooltip="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<FormatListNumbered fontSize="small" />}
          tooltip="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Block elements */}
        <ToolbarButton
          icon={<FormatQuote fontSize="small" />}
          tooltip="Blockquote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<Code fontSize="small" />}
          tooltip="Code block"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<HorizontalRule fontSize="small" />}
          tooltip="Horizontal line"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          disabled={disabled}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link */}
        <ToolbarButton
          icon={editor.isActive('link') ? <LinkOff fontSize="small" /> : <LinkIcon fontSize="small" />}
          tooltip={editor.isActive('link') ? 'Remove link' : 'Add link'}
          active={editor.isActive('link')}
          onClick={() => {
            if (editor.isActive('link')) {
              editor.chain().focus().unsetLink().run();
            } else {
              const previousUrl = editor.getAttributes('link').href || '';
              setLinkUrl(previousUrl);
              setLinkDialogOpen(true);
            }
          }}
          disabled={disabled}
        />

        {/* Image */}
        <ToolbarButton
          icon={isUploading ? <CircularProgress size={18} /> : <ImageIcon fontSize="small" />}
          tooltip="Insert image"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Emoji */}
        <ToolbarButton
          icon={<EmojiEmotions fontSize="small" />}
          tooltip="Insert emoji"
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
          disabled={disabled}
        />
      </Box>

      {/* Editor content */}
      <Box
        sx={{
          ...editorStyles.editor,
          '& .ProseMirror': {
            ...editorStyles.editor['& .ProseMirror'],
            minHeight,
            maxHeight,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Link dialog */}
      <Popover
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        anchorEl={document.body}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Insert Link
          </Typography>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSetLink(); }}>
            <input
              type="text"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 14,
                marginBottom: 12,
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setLinkDialogOpen(false)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
            </Box>
          </Box>
        </Paper>
      </Popover>

      {/* Emoji picker */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Picker
          data={data}
          onEmojiSelect={handleEmojiSelect}
          theme="light"
          previewPosition="none"
          skinTonePosition="none"
        />
      </Popover>
    </Box>
  );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;
